<template>
  <div :class="[{ '--desktop': isDesktop }, 'grouped-submissions']">
    <template v-if="isLoading || !hasCheckedAuthentication">
      <div class="d-flex">
        <div :style="{ minWidth: '44px' }">
          <placeholder-rect round full-width height="44px" class="m-0" />
        </div>
        <div :style="{ width: '100%' }" class="ml-1">
          <placeholder-rect full-width height="150px" class="m-0 mb-2" />
          <placeholder-rect full-width height="150px" class="m-0" />
        </div>
      </div>
    </template>

    <div
      v-else-if="!isAuthenticated"
      class="d-flex flex-column align-items-center px-2 py-5"
    >
      <v-icon icon="content_paste" size="2.8rem" class="text-grey-60" />
      <p class="mt-3 mb-5 font-weight-bold text-small">
        {{ $t("components.formSubmission.groupedFormSubmissions.signInTitle") }}
      </p>
      <div class="w-100">
        <v-button block to="#auth">{{
          $t("components.formSubmission.groupedFormSubmissions.signInButton")
        }}</v-button>
      </div>
    </div>

    <div v-else-if="submissions.length === 0" class="text-center p-5 my-3">
      <v-icon icon="content_paste" size="2.8rem" class="text-grey-50" />
      <div class="font-weight-semi-bold mt-3 text-large">
        {{ $t("components.formSubmission.groupedFormSubmissions.noSubmission") }}
      </div>
      <div class="mt-2 text-grey-70 px-5">
        {{ emptyDescription }}
      </div>
    </div>

    <template v-else>
      <div
        v-for="date in groupedSubmissionDates"
        :key="date"
        class="grouped-submissions__item d-flex mb-1"
      >
        <div class="grouped-submissions__date p-sticky mr-1 mb-2">
          <div class="submission-month">
            {{ toDate(date, "MMM") }}
          </div>
          <div class="submission-day">
            {{ toDate(date, "DD") }}
          </div>
        </div>
        <div class="submission-group-list flex-grow-1">
          <form-submission-table
            v-for="submission in groupedSubmissions[date]"
            :key="`${submission.id}-${loadKey}`"
            is-snippet
            :values="submission.values"
            :submission="submission"
            :groups="submission.form_groups"
            class="mb-2"
            @click="() => $emit('choose', submission)"
          />
        </div>
      </div>

      <div v-if="hasMore" class="text-center">
        <!-- TODO: Exact this pagination -->
        <template v-if="isLoadingMore">
          <div class="d-flex">
            <div :style="{ minWidth: '44px' }">
              <placeholder-rect round full-width height="44px" class="m-0" />
            </div>
            <div :style="{ width: '100%' }" class="ml-1">
              <placeholder-rect full-width height="150px" class="m-0 mb-2" />
              <placeholder-rect full-width height="150px" class="m-0" />
            </div>
          </div>
        </template>
        <v-button
          v-else
          ghost
          secondary
          class="grouped-submissions__load-more font-weight-bold"
          @click="handleFetchSubmissions"
        >
          {{ $t("components.formSubmission.groupedFormSubmissions.loadMore") }}
        </v-button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex"

import { subscribeToThread, unsubscribeFromThread } from "@/lib/pusher-utils"
import { toDate } from "@/lib/date-utils"
import EventBus, { EVENTS } from "@/lib/event-bus"

import FormSubmissionTable from "@/components/FormSubmissionTable"
import PlaceholderRect from "@/components/PlaceholderRect"

export default {
  components: {
    FormSubmissionTable,
    PlaceholderRect
  },

  props: {
    allowSingleMode: { type: Boolean, default: false },
    formId: { type: [String, Number], default: null },
    blockId: { type: [String, Number], default: null }, // Block::FormSubmissionBlock's ID
    channelId: { type: String, default: null },
    emptyDescription: { type: String, default: null }
  },

  data() {
    return {
      loadKey: 1,
      isLoading: false,
      isLoadingMore: false,
      pagination: null,
      submissions: []
    }
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapState("auth", ["hasCheckedAuthentication"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    hasMore() {
      return !!this.pagination && this.currentPage < this.pagination.totalPages
    },

    currentPage() {
      return this.pagination ? this.pagination.currentPage : 0
    },

    sortedSubmissions() {
      return this.submissions
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    groupedSubmissionDates() {
      return Object.keys(this.groupedSubmissions)
    },

    groupedSubmissions() {
      return this.sortedSubmissions.reduce((acc, submission) => {
        const date = new Date(submission.created_at).toDateString()
        ;(acc[date] = acc[date] || []).push(submission)

        return acc
      }, {})
    }
  },

  mounted() {
    EventBus.$on(
      EVENTS.SUBMISSION_VALUES_UPDATED,
      this.handleUpdateSubmissionValues
    )
  },

  beforeDestroy() {
    EventBus.$off(
      EVENTS.SUBMISSION_VALUES_UPDATED,
      this.handleUpdateSubmissionValues
    )

    if (this.channelId) {
      this.unsubscribeSubmissionChannel()
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function(value) {
        if (value) {
          this.handleFetchSubmissions()

          if (this.channelId) {
            this.subscribeSubmissionChannel()
          }
        }
      }
    }
  },

  methods: {
    toDate,

    ...mapActions("forms", ["fetchFormSubmissions"]),

    handleFetchSubmissions() {
      if (!this.formId && !this.blockId) return

      this.isLoading = this.currentPage === 0
      this.isLoadingMore = this.currentPage > 0

      this.fetchFormSubmissions({
        formId: this.formId,
        blockId: this.blockId,
        page: this.currentPage + 1
      })
        .then(data => {
          this.loadKey += 1
          this.submissions = [
            ...new Map(
              [...this.submissions, ...data.submissions].map(s => [s.id, s])
            ).values()
          ] // Remove duplicate submissions
          this.pagination = data.pagination

          if (
            this.allowSingleMode &&
            this.submissions.length === 1 &&
            !this.hasMore
          ) {
            this.$emit("choose", this.submissions[0])
          }

          this.$emit("loaded", {
            form: data.form,
            submissions: this.submissions
          })
        })
        .finally(() => {
          this.isLoading = false
          this.isLoadingMore = false
        })
    },

    handleUpdateSubmissionValues({ submissionId, values }) {
      const submission = this.submissions.find(s => s.id == submissionId)

      if (submission) {
        submission.values = values
      }
    },

    subscribeSubmissionChannel() {
      subscribeToThread(this.channelId, {
        onFormSubmitted: submission => {
          this.submissions.push(submission)
          this.loadKey += 1
        }
      })
    },

    unsubscribeSubmissionChannel() {
      unsubscribeFromThread(this.channelId)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.grouped-submissions {
  &.--desktop {
    .grouped-submissions__date {
      top: calc(1.5rem + env(safe-area-inset-top));
    }
  }

  &__date {
    background-color: $color-fixed--grey-80;
    display: flex;
    top: calc(56px + env(safe-area-inset-top)); // Toolbar height
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: start;
    min-width: 44px;
    width: 44px;
    height: 44px;
    color: $color-fixed--grey-10;
    font-size: 18px;
    font-weight: bold;
    border-radius: 50%;
    line-height: 1.1;

    .submission-month {
      font-size: 10px;
    }
  }
}
</style>
